import React, { useEffect, useRef, useState } from 'react';
import { liveQuestionApi, LiveQuestionState, QuestionAction } from '../../services/liveQuestion';

interface Props { runId: number; teacher?: boolean }

// A navigation must never retain another lesson's question or selected name.
export const LiveQuestionPanel: React.FC<Props> = props => (
  <QuestionPanel key={`${props.runId}:${Boolean(props.teacher)}`} {...props} />
);

const QuestionPanel: React.FC<Props> = ({ runId, teacher = false }) => {
  const [state, setState] = useState<LiveQuestionState | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(false);
  const accept = (next: LiveQuestionState) => setState(previous => (
    !previous || next.revision >= previous.revision ? next : previous
  ));

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const next = await liveQuestionApi.get(runId, teacher);
        if (!cancelled) accept(next);
      } catch {
        // Do not keep a previously revealed name on screen after losing sync.
        if (!cancelled && !teacher) setState(null);
      } finally {
        if (!cancelled) timeout = setTimeout(refresh, 4000);
      }
    };
    void refresh();
    return () => { cancelled = true; mounted.current = false; clearTimeout(timeout); };
  }, [runId, teacher]);

  const send = async (action: QuestionAction) => {
    if (!state || busy) return;
    setBusy(true);
    setError('');
    try {
      const next = await liveQuestionApi.command(runId, state.revision, action, action === 'show' ? draft.trim() : undefined);
      if (mounted.current) accept(next);
    } catch (err: any) {
      if (mounted.current) setError(err?.response?.data?.detail || 'Unable to update the question. Try again.');
      try {
        const next = await liveQuestionApi.get(runId, teacher);
        if (mounted.current) accept(next);
      } catch { /* Polling will reconnect; preserve the actionable error. */ }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (!teacher && !state?.question) return null;
  const disabled = busy || !state?.active;
  const button = 'rounded border border-violet-300 bg-white px-3 py-2 text-sm font-medium disabled:opacity-50';
  return (
    <section className="mb-4 rounded-xl border border-violet-200 bg-violet-50 p-4" aria-label="Verbal question">
      <p className="text-xs font-semibold uppercase tracking-wide text-violet-800">Think, then discuss</p>
      {state?.question ? <p className="mt-2 whitespace-pre-wrap text-xl font-medium text-slate-950">{state.question}</p> : null}
      {state?.revealed_name ? <p className="mt-2 text-lg text-violet-950">Let’s hear from {state.revealed_name}.</p> : null}
      {!teacher ? <p className="mt-2 text-sm text-violet-900">Prepare your thinking. Answer aloud when invited.</p> : (
        <>
          <p className="mt-2 text-sm text-violet-900">Everyone sees the question. Give think time, then choose a student to answer aloud. Continue when ready; no digital response is required.</p>
          <label className="mt-3 block text-sm font-medium" htmlFor={`verbal-question-${runId}`}>Question for the class</label>
          <textarea id={`verbal-question-${runId}`} className="mt-1 w-full rounded border p-2" maxLength={2000} rows={2} value={draft} onChange={event => setDraft(event.target.value)} />
          <div className="mt-2 flex flex-wrap gap-2">
            <button className={button} disabled={disabled || !draft.trim()} onClick={() => void send('show')}>Show question</button>
            <button className={button} disabled={disabled || !state?.question || !state.remaining_count} onClick={() => void send('draw')}>Choose random student</button>
            <button className={button} disabled={disabled || !state?.question} onClick={() => void send('clear')}>Clear question</button>
          </div>
          {state?.selected?.name ? (
            <div className="mt-3 rounded border border-violet-200 bg-white p-3">
              <p className="text-xs text-violet-800">{state.revealed_name ? 'Name shared with class' : 'Selected name · only you can see this'}</p>
              <p className="mt-1 text-lg font-semibold">{state.selected.name}</p>
              <button className={`${button} mt-2`} disabled={disabled} onClick={() => void send(state.revealed_name ? 'hide_name' : 'reveal')}>
                {state.revealed_name ? 'Hide name' : 'Reveal name to class'}
              </button>
            </div>
          ) : null}
          {state ? <p className="mt-3 text-xs text-violet-900">{state.selected_count} selected this lesson · {state.remaining_count} currently live and not yet selected. No repeats, including after clearing or changing questions.</p> : <p className="mt-2 text-sm">Connecting to question controls…</p>}
          {state?.active && state.remaining_count === 0 ? <p className="mt-2 text-sm">No unselected students are currently live. Continue the lesson or wait for another student to join.</p> : null}
          {error ? <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p> : null}
        </>
      )}
    </section>
  );
};
